import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import CreateJourney from './components/CreateJourney';
import JourneyList from './components/JourneyList';
import Settings from './components/Settings';
import { journeyAPI } from './services/api';

// Main App Component (wrapped with authentication)
function AppContent() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState('checking');

  useEffect(() => {
    if (isAuthenticated) {
      checkConnection();
      loadJourneys();
      
      // Refresh journeys every 30 seconds
      const interval = setInterval(loadJourneys, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const checkConnection = async () => {
    try {
      await journeyAPI.healthCheck();
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Connection check failed:', error);
      setConnectionStatus('disconnected');
    }
  };

  const loadJourneys = async () => {
    try {
      const response = await journeyAPI.getJourneys();
      setJourneys(response.data.journeys || []);
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Failed to load journeys:', error);
      setConnectionStatus('disconnected');
      toast.error('Failed to load journeys');
    } finally {
      setLoading(false);
    }
  };

  const handleJourneyCreated = (newJourney) => {
    setJourneys(prev => [...prev, newJourney]);
    toast.success('Journey alert registered successfully!');
  };

  const handleJourneyCompleted = async (journeyId) => {
    try {
      await journeyAPI.completeJourney(journeyId);
      setJourneys(prev => 
        prev.map(journey => 
          journey.id === parseInt(journeyId) 
            ? { ...journey, status: 'completed' }
            : journey
        )
      );
      toast.success('Welcome back! Journey completed safely.', {
        icon: '🏔️',
        duration: 5000,
      });
    } catch (error) {
      toast.error(`Failed to complete journey: ${error.message}`);
    }
  };

  const handleJourneyCancelled = async (journeyId) => {
    try {
      await journeyAPI.cancelJourney(journeyId);
      setJourneys(prev => 
        prev.map(journey => 
          journey.id === parseInt(journeyId) 
            ? { ...journey, status: 'cancelled' }
            : journey
        )
      );
      toast.success('Journey alert cancelled');
    } catch (error) {
      toast.error(`Failed to cancel journey: ${error.message}`);
    }
  };

  const handleLoginSuccess = () => {
    // This will trigger the re-render with authenticated state
    setConnectionStatus('checking');
    setLoading(true);
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Initializing Hiking Safety System...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Show loading while loading journeys
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Hiking Safety System...</p>
        </div>
      </div>
    );
  }

  // Show authenticated app
  return (
    <div className="min-h-screen bg-gray-50">
      <Header connectionStatus={connectionStatus} />
      
      <main className="container mx-auto px-4 py-8">
        <Routes>
          <Route 
            path="/" 
            element={
              <Dashboard 
                journeys={journeys} 
                onJourneyCompleted={handleJourneyCompleted}
                onJourneyCancelled={handleJourneyCancelled}
              />
            } 
          />
          <Route 
            path="/create" 
            element={
              <CreateJourney onJourneyCreated={handleJourneyCreated} />
            } 
          />
          <Route 
            path="/journeys" 
            element={
              <JourneyList 
                journeys={journeys}
                onJourneyCompleted={handleJourneyCompleted}
                onJourneyCancelled={handleJourneyCancelled}
              />
            } 
          />
          <Route path="/settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// Main App with Auth Provider and Router
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
          }} 
        />
      </AuthProvider>
    </Router>
  );
}

export default App;